import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, Clock, MapPin } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import ContactForm from '@/components/ContactForm';
import LightPageHero from '@/components/LightPageHero';

const contactItems = [
  {
    icon: Mail,
    label: 'E-post',
    value: 'info@aifinity.se',
    href: 'mailto:info@aifinity.se',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.25)',
  },
  {
    icon: Clock,
    label: 'Svarstid',
    value: 'Inom 24 timmar',
    href: null,
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.25)',
  },
  {
    icon: MapPin,
    label: 'Plats',
    value: 'Sverige',
    href: null,
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.25)',
  },
];

const contactFor = [
  'Vi automatiserar det repetitiva',
  'Vi bygger SaaS-produkter som faktiskt används',
  'Vi skriver kod, inte PowerPoints',
  'AI-chatbotar och agenter som jobbar åt dig',
  '3D-printing — från fil till fysisk produkt',
];


const Contact = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <Helmet>
        <title>Kontakta Aifinity — Berätta vad som tar för lång tid</title>
        <meta name="description" content="Första samtalet kostar ingenting. Vi lyssnar, analyserar och kommer tillbaka med ett konkret förslag inom 24 timmar." />
        <meta property="og:url" content="https://aifinity.se/contact" />
        <link rel="canonical" href="https://aifinity.se/contact" />
      </Helmet>
      <CorporateNavbar />

      <LightPageHero
        badge="Kontakt"
        heading={<>Berätta vad ni vill slippa<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>göra manuellt</span></>}
        subtext="Vi återkommer inom 24 timmar med ett konkret förslag."
      />

      {/* ── Om oss sektion (sammanslagen med Kontakt) ── */}
      <section className="py-20 bg-white border-b border-border-light">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">Om Aifinity</span>
              <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight mb-6">
                Framtidens teknik.<br />
                <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
                  Dagens resultat.
                </span>
              </h2>
              <p className="text-[15px] text-text-secondary leading-relaxed mb-4">
                Aifinity AB är ett svenskt tech-bolag med fokus på AI-automation, SaaS-produkter och
                smarta affärslösningar för moderna B2B-företag. Vi kombinerar djup teknisk kompetens med
                en pragmatisk syn på affärsnytta — vi levererar det som faktiskt fungerar.
              </p>
              <p className="text-[15px] text-text-secondary leading-relaxed mb-6">
                Vi tror på snabb leverans, mätbara resultat och lösningar som används. Från discovery
                till drift på veckor, inte månader.
              </p>
              <ul className="space-y-3">
                {[
                  'AI-agenter och automation som sparar timmar varje dag',
                  'SaaS-produkter byggda för att skala',
                  'Sömlös integration mot befintliga system',
                  'Snabba leveranser — ni ser resultat inom veckor',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-[14px] text-text-secondary">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-indigo mt-2 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Leverans inom 2–4 veckor',  desc: 'Från idé till driftsatt — inga långa uppstarter',  color: 'from-indigo-500 to-violet-600' },
                { label: 'Direktkontakt med utvecklare', desc: 'Du pratar med den som faktiskt bygger',  color: 'from-violet-500 to-purple-600' },
                { label: 'Byggt för er skala',       desc: 'Anpassat för SMB och föreningar',         color: 'from-amber-400 to-orange-500' },
                { label: 'Enkelt att testa',         desc: 'Börja litet — skala när du ser värdet',   color: 'from-emerald-400 to-teal-500' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="bg-surface-subtle rounded-2xl border border-border-light p-4 sm:p-6 text-center"
                >
                  <div className={`inline-flex w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br ${s.color} items-center justify-center mb-2 sm:mb-3`} />
                  <p className="text-sm sm:text-[15px] font-bold text-text-primary mb-1">{s.label}</p>
                  <p className="text-[11px] sm:text-xs text-text-secondary leading-tight">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact Info Cards ── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
            {contactItems.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -3 }}
                className="bg-white rounded-2xl border border-border-light p-6 shadow-sm hover:shadow-xl transition-all duration-300 text-center"
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 20px 50px ${item.glow}`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
              >
                <div className={`inline-flex w-10 h-10 rounded-xl bg-gradient-to-br ${item.gradient} items-center justify-center mb-3 shadow-md`}
                  style={{ boxShadow: `0 4px 12px ${item.glow}` }}>
                  <item.icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-widest mb-1">{item.label}</p>
                {item.href ? (
                  <a href={item.href} className="text-sm font-semibold text-text-primary hover:text-brand-indigo transition-colors">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-sm font-semibold text-text-primary">{item.value}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form + Info ── */}
      <section className="py-16 bg-surface-subtle">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-5xl mx-auto">

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-3"
            >
              <h2 className="text-2xl font-extrabold text-text-primary mb-2">Skicka ett meddelande</h2>
              <p className="text-sm text-text-secondary mb-3">Beskriv kort vad som tar tid idag. Exempel på vad du kan skriva:</p>
              <ul className="space-y-1.5 mb-6">
                {[
                  '"Vi får många mail vi inte hinner svara på"',
                  '"Bokningar sker manuellt idag"',
                  '"Vi vill automatisera kundsupport"',
                ].map(ex => (
                  <li key={ex} className="text-[13px] text-text-muted italic pl-3 border-l-2 border-indigo-200">{ex}</li>
                ))}
              </ul>
              <div className="bg-white rounded-2xl border border-border-light shadow-sm p-5 sm:p-8">
                <ContactForm />
              </div>
            </motion.div>

            {/* Right info */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-extrabold text-text-primary mb-6">Vi hjälper er med</h2>
              <div className="bg-white rounded-2xl border border-border-light shadow-sm p-6 mb-6">
                <ul className="space-y-3">
                  {contactFor.map(item => (
                    <li key={item} className="flex items-start gap-3 text-[15px] text-text-secondary">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-indigo mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50">
                <p className="text-sm font-semibold text-text-primary mb-1">Snabb respons garanterat</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  Vi svarar på alla förfrågningar inom 24 timmar på vardagar. Bråttom? Maila direkt till{' '}
                  <a href="mailto:info@aifinity.se" className="text-brand-indigo font-semibold">info@aifinity.se</a>.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default Contact;
